import { Injectable } from '@angular/core';
import { ActionplanAnalyticsService } from '@solvace/core/services/analytics/actionplan';
import { ChecklistAnalyticsService } from '@solvace/core/services/analytics/checklist';
import { CilAnalyticsService } from '@solvace/core/services/analytics/cil';
import { DefecttagAnalyticsService } from '@solvace/core/services/analytics/defecttag';
import { UserIndicator, UserIndicatorsService } from 'projects/lib-shared-core/src/public-api';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  public get actionPlanIndicator$() {
    return this._actionPlanAnalyticsService._indicator$;
  }
  public get checklistAnalyticsIndicator$() {
    return this._checklistAnalyticsService._indicator$;
  }
  public get cilAnalyticsIndicator$() {
    return this._cilAnalyticsService._indicator$;
  }
  public get defectTagAnalyticsIndicator$() {
    return this._defectTagAnalyticsService._indicator$;
  }

  public get subordinatesActionPlanIndicator$() {
    return this._actionPlanAnalyticsService._subordinatesIndicator$;
  }
  public get subordinatesChecklistAnalyticsIndicator$() {
    return this._checklistAnalyticsService._subordinatesIndicator$;
  }
  public get subordinatesCilAnalyticsIndicator$() {
    return this._cilAnalyticsService._subordinatesIndicator$;
  }
  public get subordinatesDefectTagAnalyticsIndicator$() {
    return this._defectTagAnalyticsService._subordinatesIndicator$;
  }

  constructor(
    private _actionPlanAnalyticsService: ActionplanAnalyticsService,
    private _checklistAnalyticsService: ChecklistAnalyticsService,
    private _cilAnalyticsService: CilAnalyticsService,
    private _defectTagAnalyticsService: DefecttagAnalyticsService,
    private _userIndicatorService: UserIndicatorsService
  ) {}

  private _updateChecklist(indicator: Partial<UserIndicator>) {
    this._userIndicatorService.updateChecklistIndicators(indicator);
  }

  private _updateLil(indicator: Partial<UserIndicator>) {
    this._userIndicatorService.updateLilIndicators(indicator);
  }

  private _updateActionPlan(indicator: Partial<UserIndicator>) {
    this._userIndicatorService.updateActionPlanIndicators(indicator);
  }

  private _updateDefectTag(indicator: Partial<UserIndicator>) {
    this._userIndicatorService.updateDefectTagIndicators(indicator);
  }

  public getUserIndicators(only?: { checklist?: boolean; lil?: boolean; actionPlan?: boolean; defectTag?: boolean }) {
    if (only && Object.keys(only).some((key) => only[key])) {
      only = {
        checklist: only.checklist ?? false,
        lil: only.lil ?? false,
        actionPlan: only.actionPlan ?? false,
        defectTag: only.defectTag ?? false,
      };
    }

    if (only?.checklist ?? true) {
      this._updateChecklist({ loading: true });
      this._checklistAnalyticsService.getInspectionIndicators().subscribe({
        next: (indicator) => {
          this._updateChecklist({
            amount: indicator.data.sumTotal,
            loading: false,
          });
        },
        error: (error) => {
          this._updateChecklist({
            loading: false,
            error: true,
          });
          return error;
        },
      });
    }

    if (only?.lil ?? true) {
      this._updateLil({ loading: true });
      this._cilAnalyticsService.getInspectionIndicators().subscribe({
        next: (indicator) => {
          this._updateLil({
            amount: indicator.data.sumTotal,
            loading: false,
          });
        },
        error: (error) => {
          this._updateLil({
            loading: false,
            error: true,
          });
          return error;
        },
      });
    }

    if (only?.actionPlan ?? true) {
      this._updateActionPlan({ loading: true });
      this._actionPlanAnalyticsService.getActionPlanIndicators().subscribe({
        next: (indicator) => {
          this._updateActionPlan({
            amount: indicator.data.sumTotal,
            loading: false,
          });
        },
        error: (error) => {
          this._updateActionPlan({
            loading: false,
            error: true,
          });
          return error;
        },
      });
    }

    if (only?.defectTag ?? true) {
      this._updateDefectTag({ loading: true });
      this._defectTagAnalyticsService.getDefectTagIndicators().subscribe({
        next: (indicator) => {
          this._updateDefectTag({
            amount: indicator.data.sumTotal,
            loading: false,
          });
        },
        error: (error) => {
          this._updateDefectTag({
            loading: false,
            error: true,
          });
          return error;
        },
      });
    }
  }

  public getSubordinatesIndicators(only?: { checklist?: boolean; lil?: boolean; actionPlan?: boolean; defectTag?: boolean }) {
    if (only && Object.keys(only).some((key) => only[key])) {
      only = {
        checklist: only.checklist ?? false,
        lil: only.lil ?? false,
        actionPlan: only.actionPlan ?? false,
        defectTag: only.defectTag ?? false,
      };
    }
    if (only?.checklist ?? true) {
      this._updateChecklist({ loading: true });
      this._checklistAnalyticsService.getInspectionSubordinateIndicators().subscribe({
        next: (indicator) => {
          this._updateChecklist({
            amount: indicator.data.sumTotal,
            loading: false,
          });
        },
        error: (error) => {
          this._updateChecklist({
            loading: false,
            error: true,
          });
          return error;
        },
      });
    }

    if (only?.lil ?? true) {
      this._updateLil({ loading: true });
      this._cilAnalyticsService.getInspectionSubordinatesIndicators().subscribe({
        next: (indicator) => {
          this._updateLil({
            amount: indicator.data.sumTotal,
            loading: false,
          });
        },
        error: (error) => {
          this._updateLil({
            loading: false,
            error: true,
          });
          return error;
        },
      });
    }

    if (only?.actionPlan ?? true) {
      this._updateActionPlan({ loading: true });
      this._actionPlanAnalyticsService.getActionPlanSubordinatesIndicators().subscribe({
        next: (indicator) => {
          this._updateActionPlan({
            amount: indicator.data.sumTotal,
            loading: false,
          });
        },
        error: (error) => {
          this._updateActionPlan({
            loading: false,
            error: true,
          });
          return error;
        },
      });
    }

    if (only?.defectTag ?? true) {
      this._updateDefectTag({ loading: true });
      this._defectTagAnalyticsService.getDefectTagSubordinatesIndicators().subscribe({
        next: (indicator) => {
          this._updateDefectTag({
            amount: indicator.data.sumTotal,
            loading: false,
          });
        },
        error: (error) => {
          this._updateDefectTag({
            loading: false,
            error: true,
          });
          return error;
        },
      });
    }
  }
}
